import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9328okk00l6eltrof0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, typeof value === "string" ? value : "");
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-2xl text-foreground-950">
          <i className="ri-check-line"></i>
        </div>
        <h3 className="mt-4 text-lg font-bold text-foreground-950">提交成功</h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          感谢你的留言，我们已收到反馈，会尽快与取得联系。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-edit-line"></i>
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-1 text-sm text-foreground-600">填写下方信息，我们会尽快回复你的咨询与建议。</p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的姓名"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          required
          maxLength={500}
          placeholder="请输入你的留言（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <div className="contact-form-extra">
        <label htmlFor="contact-company-alt">Company</label>
        <input
          id="contact-company-alt"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status === "error" && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            正在提交…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交留言
          </>
        )}
      </button>
      <p className="mt-3 text-xs text-foreground-400">
        提交后我们将收到你的留言，用于后续联系与回复。
      </p>
    </form>
  );
}